import MuleBadge from '../../components/MuleBadge';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import { OrderDispatcherFilter, DriverDispatcherFilter } from '../../components/filters';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import FilterBar from '../../components/FilterBar';
import DateTimePicker from '../../components/DateTimePicker';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import { useGlobalFilters } from '../../contexts/GlobalFiltersContext';
import { useToast } from '../../contexts/ToastContext';
import ordersService from '../../services/orders.service';
import api from '../../services/api';
import { config } from '../../config/config';

interface DeclinedDispatch {
  dispatch_recno: number;
  pronumber: string;
  origin: string;
  destination: string;
  unitnumber: string;
  driver_name: string;
  isMule?: boolean;
  rejectdatetime: string | null;
  rejectreason: string;
  rejectcomment: string;
  createdatetime: string | null;
  estimateddriverpay: number | null;
  driver_dispatcher: string;
  driver_dispatcher_username: string;
  region: string;
  order_dispatcher: string;
  order_dispatcher_username: string;
  resolvedatetime: string | null;
  resolvecomment: string;
  resolvedby: string;
}

// Format date as YYYY-MM-DD
const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format datetime for display
const formatDateTime = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

// Format currency
const formatCurrency = (value: number | null): string => {
  if (value == null) return '-';
  return `$${value.toFixed(2)}`;
};

function DeclinedDispatchesContent() {
  // Any orders-scope registry field can be added as a column here; the backend
  // attaches it to the rows this report already returned, keyed on pronumber.
  const fieldSelection = useAdditionalFieldSelection('declined-dispatches');
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const fieldsReady = fieldSelection.ready;
  // Data state
  const [data, setData] = useState<DeclinedDispatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Independent start/end date pickers (mirrors DriverEndpoints' free-at
  // split). Defaults to the last 30 days; either can be cleared but both
  // must be set for the server-side date filter to fire.
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => new Date());

  // "Show resolved" is off by default: the server only ships unresolved legs
  // unless asked (include_resolved=true).
  const [showResolved, setShowResolved] = useState(false);

  // Per-row resolve edits keyed by dispatch_recno; only ticked rows are posted.
  // Read through refs inside cell renderers so the columns array stays stable
  // (same reason as the Not Validated Chassis card on the Dispatch Dashboard).
  type ResolveEdit = { resolve: boolean; comment: string };
  const [resolveEdits, setResolveEdits] = useState<Record<string, ResolveEdit>>({});
  const [resolveSaving, setResolveSaving] = useState(false);
  // TanTable is memo'd (shallow props compare). Cells read edits via these
  // refs, so a parent re-render must reach TanTable for ticks to show. The
  // inline `defaultSorting` literal on <TanTable> is what currently guarantees
  // that; if you hoist it to a constant, pass something that changes with
  // `resolveEdits` instead.
  const resolveEditsRef = useRef<Record<string, ResolveEdit>>({});
  const resolveSavingRef = useRef(false);
  resolveEditsRef.current = resolveEdits;
  resolveSavingRef.current = resolveSaving;
  const { showToast } = useToast();

  const setResolveTicked = useCallback((recno: string, resolve: boolean) => {
    setResolveEdits((prev) => ({ ...prev, [recno]: { ...(prev[recno] ?? { resolve: false, comment: '' }), resolve } }));
  }, []);
  const setResolveComment = useCallback((recno: string, comment: string) => {
    setResolveEdits((prev) => ({ ...prev, [recno]: { ...(prev[recno] ?? { resolve: false, comment: '' }), comment } }));
  }, []);

  // Global filters
  const {
    selectedRegions,
    setSelectedRegions,
    selectedOrderDispatchers,
    setSelectedOrderDispatchers,
    selectedDriverDispatchers,
    setSelectedDriverDispatchers,
    isLoading: globalFiltersLoading,
  } = useGlobalFilters();

  // Region options
  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);

  // Order details drawer state — ProDetailSlideOver fetches/refreshes the
  // order itself; we only track which PRO is open.
  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  // Fetch region options on mount
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const regionsRes = await ordersService.getRegions();
        setRegionOptions(
          regionsRes.data.regions.map((region: string) => ({
            value: region,
            label: region,
          }))
        );
      } catch (error) {
        console.error('Failed to fetch regions:', error);
      }
    };
    fetchRegions();
  }, []);

  // Fetch data
  const fetchData = useCallback(async (showLoading = false) => {
    if (!isMounted.current) return;
    if (!startDate || !endDate) return;

    // Cancel any in-flight request to prevent stale responses
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setError(null);
      setData([]);
      if (showLoading) setLoading(true);

      const params: Record<string, string> = {
        start_date: formatDateKey(startDate),
        end_date: formatDateKey(endDate),
      };
      if (selectedOrderDispatchers.length > 0) {
        params.order_dispatcher = selectedOrderDispatchers.join(',');
      }
      if (selectedDriverDispatchers.length > 0) {
        params.driver_dispatcher = selectedDriverDispatchers.join(',');
      }
      if (selectedRegions.length > 0) {
        params.region = selectedRegions.join(',');
      }
      if (showResolved) params.include_resolved = 'true';

      // The saved selection is part of the request; going early just fetches twice.
      if (!fieldsReady) return;
      if (additionalFieldsKey) params.additional_fields = additionalFieldsKey;

      const response = await api.get('/reporting/declined-dispatches', {
        params,
        signal: controller.signal,
      });
      if (!isMounted.current || controller.signal.aborted) return;
      setData(response.data);
      setResolveEdits({});
    } catch (err) {
      if (!isMounted.current || controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      if (isMounted.current && !controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [startDate, endDate, selectedOrderDispatchers, selectedDriverDispatchers, selectedRegions, showResolved, fieldsReady, additionalFieldsKey]);

  // Fetch on filter changes
  useEffect(() => {
    if (globalFiltersLoading) return;
    fetchData(true);
  }, [fetchData, globalFiltersLoading]);

  // Open order details drawer
  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  const pendingResolve = useMemo(() => Object.entries(resolveEdits).filter(([, e]) => e.resolve), [resolveEdits]);

  const handleSaveResolves = useCallback(async () => {
    if (pendingResolve.length === 0) return;
    const byRecno = new Map(data.map((r) => [String(r.dispatch_recno), r]));
    const rows = pendingResolve
      .map(([recno, edit]) => {
        const row = byRecno.get(recno);
        return row ? { dispatch_recno: row.dispatch_recno, pronumber: Number(row.pronumber), comment: edit.comment } : null;
      })
      .filter((r): r is NonNullable<typeof r> => r !== null);
    if (rows.length === 0) return;
    setResolveSaving(true);
    try {
      const res = await api.post('/dispatch-dashboard/resolve-declined', { rows });
      const { resolved = 0, skipped = 0 } = res.data || {};
      showToast({ type: skipped ? 'warning' : 'success', message: `${resolved} resolved${skipped ? `, ${skipped} already resolved` : ''}.` });
      setResolveEdits({});
      fetchData(false);
    } catch (err: any) {
      showToast({ type: 'error', message: err?.response?.data?.detail || 'Failed to resolve declined legs.' });
    } finally {
      setResolveSaving(false);
    }
  }, [pendingResolve, data, fetchData, showToast]);

  // Column definitions
  const baseColumns = useMemo<ColumnDef<DeclinedDispatch>[]>(() => [
    {
      accessorKey: 'pronumber',
      header: 'PRO #',
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const pronumber = row.original.pronumber;
        // Viewed/last-viewed highlight + click-to-mark is applied by TanTable's
        // trackViewedColumnId="pronumber" wrapper.
        return (
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openOrderDrawer(Number(pronumber));
              }}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
            >
              {pronumber}
            </button>
            <a
              href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              title="Open in CMSV1"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </a>
          </div>
        );
      },
    },
    {
      id: 'driver_name',
      accessorFn: (row) => `${row.driver_name || ''} ${row.unitnumber || ''}`.trim(),
      header: 'Driver',
      meta: { exportValue: (row: { original: DeclinedDispatch }) => `${row.original.driver_name || ''} ${row.original.unitnumber || ''}${row.original.isMule ? ' (Mule)' : ''}` },
      filterFn: 'arrIncludes',
      cell: ({ row }) => (
        <div>
          <div className="text-sm">{row.original.driver_name || '-'}<MuleBadge isMule={row.original.isMule} /></div>
          {row.original.unitnumber && (
            <div className="text-xs text-muted">{row.original.unitnumber}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'origin',
      header: 'Origin',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'destination',
      header: 'Destination',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'rejectreason',
      header: 'Reason',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'rejectcomment',
      header: 'Comment',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'rejectdatetime',
      header: 'Reject Time',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDateTime(row.original.rejectdatetime),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'createdatetime',
      header: 'Created Time',
      // When the DECLINE was recorded, not the order's `createdatetime` its id
      // collides with — so it keeps its own label.
      meta: { registryTitle: false },
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDateTime(row.original.createdatetime),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'estimateddriverpay',
      header: 'Est. Driver Pay',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.estimateddriverpay),
    },
    {
      id: 'resolve',
      header: 'Resolve',
      enableSorting: false,
      enableColumnFilter: false,
      meta: { exportValue: (r: { original: DeclinedDispatch }) => (r.original.resolvedby ? 'Y' : '') },
      cell: ({ row }) => {
        if (row.original.resolvedby) return <span className="text-sm" title="Resolved" aria-label="Resolved">✓</span>;
        const recno = String(row.original.dispatch_recno);
        const edit = resolveEditsRef.current[recno] ?? { resolve: false, comment: '' };
        return (
          <input
            type="checkbox"
            aria-label={`Resolve ${row.original.pronumber}`}
            checked={edit.resolve}
            disabled={resolveSavingRef.current}
            onChange={(e) => setResolveTicked(recno, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      id: 'resolve_comment',
      accessorKey: 'resolvecomment',
      header: 'Resolve Comment',
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        if (row.original.resolvedby) return <span className="text-sm">{row.original.resolvecomment || '-'}</span>;
        const recno = String(row.original.dispatch_recno);
        const edit = resolveEditsRef.current[recno] ?? { resolve: false, comment: '' };
        return (
          <input
            type="text"
            aria-label={`Resolve comment ${row.original.pronumber}`}
            value={edit.comment}
            disabled={!edit.resolve || resolveSavingRef.current}
            maxLength={255}
            onChange={(e) => setResolveComment(recno, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className={`w-full text-xs border rounded px-2 py-0.5 border-gray-300 dark:border-gray-600 bg-panel focus:outline-none focus:ring-1 focus:ring-blue-500 ${!edit.resolve ? 'cursor-not-allowed' : ''}`}
          />
        );
      },
    },
    {
      accessorKey: 'resolvedatetime',
      header: 'Resolved',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDateTime(row.original.resolvedatetime),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'resolvedby',
      header: 'Resolved By',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
  ], [openOrderDrawer, setResolveTicked, setResolveComment]);

  // Rows are report rows, not orders — several can share a pro — so an added order
  // column repeats down them rather than blanking.
  const { columns, availableFields } = useAdditionalFieldColumns<DeclinedDispatch>(
    fieldSelection, { baseColumns },
  );

  const getRowId = useCallback((row: DeclinedDispatch) => {
    return String(row.dispatch_recno);
  }, []);

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <FilterBar label="">
          <div className="flex items-center gap-1">
            <span>Declined between</span>
            <DateTimePicker
              mode="date"
              selectionType="single"
              value={startDate}
              onChange={(val) => setStartDate((val as Date | null) ?? null)}
              inputVariant="compact"
              labelType="none"
              placeholder="Start"
              autoApply
            />
            <span>and</span>
            <DateTimePicker
              mode="date"
              selectionType="single"
              value={endDate}
              onChange={(val) => setEndDate((val as Date | null) ?? null)}
              inputVariant="compact"
              labelType="none"
              placeholder="End"
              autoApply
            />
          </div>
        </FilterBar>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <FilterBar label="">
        <div className="flex items-center gap-1">
          <span>Declined between</span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={startDate}
            onChange={(val) => setStartDate((val as Date | null) ?? null)}
            inputVariant="compact"
            labelType="none"
            placeholder="Start"
            autoApply
          />
          <span>and</span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={endDate}
            onChange={(val) => setEndDate((val as Date | null) ?? null)}
            inputVariant="compact"
            labelType="none"
            placeholder="End"
            autoApply
          />
        </div>
        <FilterBar.Divider />
        <OrderDispatcherFilter
          value={selectedOrderDispatchers}
          onChange={setSelectedOrderDispatchers}
        />
        <DriverDispatcherFilter
          value={selectedDriverDispatchers}
          onChange={setSelectedDriverDispatchers}
        />
        <FilterBar.Divider />
        <MultiSelect
          options={regionOptions}
          value={selectedRegions}
          onChange={setSelectedRegions}
          placeholder="All Regions"
          showClearOption={true}
          clearOptionLabel="All Regions"
          size="sm"
        />
        <FilterBar.Divider />
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
          Show resolved
        </label>
        <button
          type="button"
          onClick={handleSaveResolves}
          disabled={pendingResolve.length === 0 || resolveSaving || loading}
          className={`text-xs px-3 py-1 rounded font-medium ${pendingResolve.length > 0 && !resolveSaving ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-slate-600 text-gray-400 cursor-not-allowed'}`}
        >
          {resolveSaving ? 'Saving…' : pendingResolve.length > 0 ? `Save (${pendingResolve.length})` : 'Save'}
        </button>
      </FilterBar>

      <div className="px-4 py-2 text-sm text-muted bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
        This table lists dispatches that drivers declined. Tick Resolve (and optionally add a comment) then Save to acknowledge a decline; resolved legs are hidden unless "Show resolved" is on.
      </div>

      <div className="flex-1 min-h-0 p-4">
        <TanTable
          data={data}
          columns={columns}
          availableFields={availableFields}
          additionalFields={fieldSelection.additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          trackViewedColumnId="pronumber"
          loading={loading}
          // Inline literal on purpose: TanTable is memo'd and the resolve cells
          // read edit state via refs, so this changing identity each render is
          // what lets ticks/comments show. Hoisting it would need a replacement
          // prop that changes with `resolveEdits`.
          defaultSorting={[{ id: 'rejectdatetime', desc: true }]}
          settingsKey="declined-dispatches"
          enableColumnFilters={true}
          enableGrouping={true}
          getRowId={getRowId}
          cardTitle="Declined Dispatches"
          fillContainer={true}
        />
      </div>

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />
    </div>
  );
}

function DeclinedDispatches() {
  return (
    <ViewedProsProvider>
      <DeclinedDispatchesContent />
    </ViewedProsProvider>
  );
}

export default DeclinedDispatches;
